import type { JSX } from 'react';
import { useEffect, useState, useRef } from 'react';
import Loader from '@/components/Loader';
import { TabSlider } from '@/components/ui/TabSlider';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { useLocation } from 'react-router-dom';
import RatingsBarChart from '@/pages/user/components/RatingsBarChart';
import AudioVisualizer from '@/pages/user/components/AudioVisualizer';
import { useLazyGetPredictionAudioQuery } from '@/store/api/PredictionAudio/predictionaudioApi';

import { useLazyGetPredictionByPeriodQuery } from '@/store/api/PredictionPeriod/predictionperiodApi';

import { useLazyGetWeeklyPredictionQuery } from '@/store/api/WeeklyPredictions/weeklyPredictionApi';
import { useLazyGetNextWeekPredictionQuery } from '@/store/api/WeeklyPredictions/NextWeeklyPredictionApi';
import { useLazyGetMonthlyPredictionQuery } from '@/store/api/MonthlyPredictions/monthlyPredictionApi';
import { useLazyGetNextMonthlyPredictionQuery } from '@/store/api/MonthlyPredictions/NextMonthlyPredictionApi';
import Modal from '@/utils/modal.utils';
import DailyPredictionsView from '@/pages/user/components/DailyPredictions';
import { getUserMoonSign } from '@/utils/dailyPredictions';

interface HoroscopeData {
  title: string;
  content: string;
}

const RASHIS = [
  { en: 'Aries', hi: 'Mesha' },
  { en: 'Taurus', hi: 'Vrishabha' },
  { en: 'Gemini', hi: 'Mithuna' },
  { en: 'Cancer', hi: 'Karkataka' },
  { en: 'Leo', hi: 'Simha' },
  { en: 'Virgo', hi: 'Kanya' },
  { en: 'Libra', hi: 'Tula' },
  { en: 'Scorpio', hi: 'Vrishchika' },
  { en: 'Sagittarius', hi: 'Dhanur' },
  { en: 'Capricorn', hi: 'Makar' },
  { en: 'Aquarius', hi: 'Kumbha' },
  { en: 'Pisces', hi: 'Meena' },
];

const formatWeeklyPrediction = (parsed: any): HoroscopeData[] => {
  if (!parsed) return [];

  const normalizeTitle = (key: string): string => {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const cleanBullet = (text: string) => text.replace(/^[•\-–]\s*/, '').trim();

  const formatted: HoroscopeData[] = [];

  const heading =
    parsed.heading ||
    parsed.overview?.heading ||
    parsed.general_predictions?.heading ||
    (parsed.prediction &&
      (parsed.prediction.heading ||
        parsed.prediction.overview?.heading ||
        parsed.prediction.general_predictions?.heading));

  // Use HEADING for the centered date range
  if (heading) {
    formatted.push({ title: 'HEADING', content: heading });
  }

  Object.entries(parsed).forEach(([key, value]: any) => {
    if (
      ['ratings', 'heading', 'title', 'monthly_forecast', 'videoId', 'videoUrl', 'userId'].includes(
        key
      )
    )
      return;

    // Handle Overview / Sector Objects
    if (
      (key === 'overview' || key === 'general_predictions') &&
      typeof value === 'object' &&
      value !== null
    ) {
      Object.entries(value).forEach(([subKey, subValue]: any) => {
        if (subKey === 'heading') return;
        formatted.push({
          title: normalizeTitle(subKey),
          content: typeof subValue === 'string' ? subValue : JSON.stringify(subValue),
        });
      });
      return;
    }

    // Handle Nesting (e.g., personal_creative_life) - Keep Parent Header
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      key !== 'key_days'
    ) {
      formatted.push({ title: normalizeTitle(key), content: '' }); // Parent Header
      Object.entries(value).forEach(([subKey, subValue]: any) => {
        formatted.push({
          title: normalizeTitle(subKey),
          content: typeof subValue === 'string' ? subValue : JSON.stringify(subValue),
        });
      });
      return;
    }

    if (key === 'key_days' && value) {
      formatted.push({
        title: 'KEY DAYS',
        content: JSON.stringify({
          type: 'grouped-bullets',
          groups: [
            value.favourable_days?.length
              ? { subtitle: 'Favourable Days', items: value.favourable_days.map(cleanBullet) }
              : null,
            value.caution_days?.length
              ? { subtitle: 'Caution Days', items: value.caution_days.map(cleanBullet) }
              : null,
          ].filter(Boolean),
        }),
      });
      return;
    }

    if (Array.isArray(value)) {
      formatted.push({
        title: normalizeTitle(key),
        content: JSON.stringify({ type: 'bullets', items: value.map(cleanBullet) }),
      });
      return;
    }

    if (typeof value === 'object' && value !== null) {
      formatted.push({ title: normalizeTitle(key), content: '' });
      Object.entries(value).forEach(([subKey, subValue]: any) => {
        const content = Array.isArray(subValue)
          ? JSON.stringify({ type: 'bullets', items: subValue.map(cleanBullet) })
          : typeof subValue === 'string'
            ? subValue
            : JSON.stringify(subValue);

        formatted.push({
          title: normalizeTitle(subKey),
          content,
        });
      });
      return;
    }

    if (typeof value === 'string') {
      formatted.push({ title: normalizeTitle(key), content: value });
      return;
    }
  });

  return formatted;
};

const formatMonthlyPrediction = (rawParsed: any): HoroscopeData[] => {
  if (!rawParsed) return [];

  const normalizeTitle = (key: string): string => {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const cleanBullet = (text: string) => text.replace(/^[•\-–]\s*/, '').trim();

  const formatted: HoroscopeData[] = [];

  let mainData = rawParsed.prediction || rawParsed;
  if (mainData.prediction) mainData = mainData.prediction;
  if (mainData.predictions) mainData = mainData.predictions;

  const heading =
    rawParsed.heading || mainData.heading || mainData.title || mainData.monthly_forecast;
  if (heading) {
    formatted.push({ title: 'HEADING', content: heading });
  }

  Object.entries(mainData).forEach(([key, value]: any) => {
    if (
      ['ratings', 'heading', 'title', 'monthly_forecast', 'videoId', 'videoUrl', 'userId'].includes(
        key
      )
    )
      return;

    // Handle strings
    if (typeof value === 'string' && key !== 'content') {
      formatted.push({ title: normalizeTitle(key), content: value });
      return;
    }

    // Handle "content" key
    if (key === 'content' && typeof value === 'string') {
      formatted.push({ title: 'Overview', content: value });
      return;
    }

    // Handle Objects (Sectors or Phases)
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Caution & Remedies
      if (
        key === 'caution_remedies' ||
        key === 'caution_and_remedies' ||
        key === 'periods_of_caution_and_remedies'
      ) {
        formatted.push({
          title: 'CAUTION & REMEDIES',
          content: JSON.stringify({
            type: 'grouped-bullets',
            groups: [
              (value.periods_of_caution || value.caution_periods)?.length
                ? {
                  subtitle: 'Caution Periods',
                  items: Array.isArray(value.periods_of_caution || value.caution_periods)
                    ? (value.periods_of_caution || value.caution_periods).map(cleanBullet)
                    : [cleanBullet(value.periods_of_caution || value.caution_periods)],
                }
                : null,
              (value.remedies || value.suggested_remedies)?.length
                ? {
                  subtitle: 'Remedies',
                  items: Array.isArray(value.remedies || value.suggested_remedies)
                    ? (value.remedies || value.suggested_remedies).map(cleanBullet)
                    : [cleanBullet(value.remedies || value.suggested_remedies)],
                }
                : null,
            ].filter(Boolean),
          }),
        });
        return;
      }

      // Preserve Parent Header
      formatted.push({ title: normalizeTitle(key), content: '' });

      // Standard nested
      Object.entries(value).forEach(([subKey, subValue]: any) => {
        if (subKey === 'title' || subKey === 'heading') return;

        const content = Array.isArray(subValue)
          ? JSON.stringify({ type: 'bullets', items: subValue.map(cleanBullet) })
          : typeof subValue === 'string'
            ? subValue
            : JSON.stringify(subValue);

        formatted.push({
          title: normalizeTitle(subKey),
          content,
        });
      });
      return;
    }

    if (Array.isArray(value)) {
      formatted.push({
        title: normalizeTitle(key),
        content: JSON.stringify({ type: 'bullets', items: value.map(cleanBullet) }),
      });
      return;
    }
  });

  return formatted;
};

export default function GenPrediction({ userId }: { userId: string }): JSX.Element {
  const { state } = useLocation();
  const user = state?.user;

  const rashi = user?.BirthDetail?.astroData?.sign;
  const [activeRashi, setActiveRashi] = useState<string | null>(null);

  useEffect(() => {
    if (rashi) {
      setActiveRashi(rashi);
    }
  }, [rashi]);

  const [ratings, setRatings] = useState<any>(null);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [predictionCharts, setPredictionCharts] = useState<{
    south?: string;
    north?: string;
  } | null>(null);

  const [monthlyView, setMonthlyView] = useState<'current' | 'next'>('current');

  const [getPredictionAudio] = useLazyGetPredictionAudioQuery();
  const [getPredictionPeriod] = useLazyGetPredictionByPeriodQuery();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<string[]>([]);
  const [selectedTitle, setSelectedTitle] = useState('');
  const [predictionData, setPredictionData] = useState<HoroscopeData[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [textStatus, setTextStatus] = useState<'IDLE' | 'PENDING' | 'SUCCESS' | 'FAILED'>('IDLE');
  const [uiLoading, setUiLoading] = useState(true);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [predictionId, setPredictionId] = useState<string | null>(null);
  const audioPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (audioPollingTimeoutRef.current) clearTimeout(audioPollingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (videoUrl) {
      setIsVideoLoading(true);
    }
  }, [videoUrl]);

  const fetchAudioWithPolling = async (predictionId: string, retryCount = 0) => {
    if (audioPollingTimeoutRef.current) clearTimeout(audioPollingTimeoutRef.current);
    setAudioLoading(true);
    setAudioError(null);
    try {
      const audioRes = await getPredictionAudio({
        predictionId,
        language: 'English',
      }).unwrap();

      const rawStatus = audioRes?.status;
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      if (audioRes?.url) {
        setAudioSrc(audioRes.url);
        setAudioLoading(false);
        return;
      }

      if (status === 'DRAFT') {
        setAudioError(audioRes?.message || 'Audio not generated yet');
        setAudioLoading(false);
        return;
      }

      if (status === 'FAILED') {
        if (retryCount < 4) {
          audioPollingTimeoutRef.current = setTimeout(
            () => fetchAudioWithPolling(predictionId, retryCount + 1),
            2000
          );
        } else {
          setAudioError('Audio generation failed.');
          setAudioLoading(false);
        }
        return;
      }

      // If pending or no url, poll after 2 seconds
      audioPollingTimeoutRef.current = setTimeout(
        () => fetchAudioWithPolling(predictionId, retryCount),
        2000
      );
    } catch (err: any) {
      console.error('Audio fetch error:', err);

      const rawStatus = err?.data?.status;
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      if (status === 'DRAFT') {
        setAudioError(err?.data?.message || 'Audio not generated yet');
        setAudioLoading(false);
        return;
      }

      if (status === 'FAILED') {
        if (retryCount < 4) {
          audioPollingTimeoutRef.current = setTimeout(
            () => fetchAudioWithPolling(predictionId, retryCount + 1),
            2000
          );
        } else {
          setAudioError('Audio generation failed.');
          setAudioLoading(false);
        }
        return;
      }

      // Handle server-side 500 error or other failed generations
      if (
        err?.status === 500 ||
        rawStatus === 500 ||
        String(err?.data?.message || '')
          .toLowerCase()
          .includes('fail')
      ) {
        setAudioError(err?.data?.message || 'Audio failed to generate.');
        setAudioLoading(false);
        return;
      }

      if (retryCount < 5) {
        audioPollingTimeoutRef.current = setTimeout(
          () => fetchAudioWithPolling(predictionId, retryCount + 1),
          2000
        );
      } else {
        setAudioError(err?.data?.message || 'Audio generation failed. Please retry.');
        setAudioLoading(false);
      }
    }
  };

  const visualLoading = uiLoading;

  const [activeTab, setActiveTab] = useState<
    'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'
  >('daily');

  const [error, setError] = useState<boolean>(false);
  const [weeklyView, setWeeklyView] = useState<'current' | 'next'>('current');

  const [getWeeklyPrediction, weeklyState] = useLazyGetWeeklyPredictionQuery();
  const [getNextWeekPrediction, nextWeekState] = useLazyGetNextWeekPredictionQuery();

  const [getMonthlyPrediction, monthlyState] = useLazyGetMonthlyPredictionQuery();
  const [getNextMonthlyPrediction, nextMonthlyState] = useLazyGetNextMonthlyPredictionQuery();

  const loading =
    weeklyState.isFetching ||
    nextWeekState.isFetching ||
    monthlyState.isFetching ||
    nextMonthlyState.isFetching;

  const formatPredictionText = (raw: string): HoroscopeData[] => {
    if (!raw) return [];

    const sections = raw
      .split(/\n\s*\n/)
      .map((p) => p.trim())
      .filter(Boolean);

    const formatted: HoroscopeData[] = [];

    sections.forEach((block) => {
      const lines = block.split('\n').filter(Boolean);
      if (!lines.length || !lines[0]) return;

      const title = lines[0]?.trim() ?? '';
      const rest = lines.slice(1);

      const hasBullets = rest.join('\n').includes('•');

      if (hasBullets) {
        const bullets = rest
          .join('\n')
          .split('•')
          .map((b) => b.trim())
          .filter(Boolean);

        formatted.push({
          title,
          content: JSON.stringify({
            type: 'bullets',
            items: bullets,
          }),
        });
      } else {
        formatted.push({
          title,
          content: rest.join(' ').trim(),
        });
      }
    });

    return formatted;
  };

  type TabType = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual';

  const isComingSoonTab = (tab: TabType): tab is 'quarterly' | 'annual' => {
    return tab === 'quarterly' || tab === 'annual';
  };
  const openModal = (images: (string | undefined)[], title: string) => {
    setModalImages(images.filter(Boolean) as string[]);
    setSelectedTitle(title);
    setModalOpen(true);
  };
  const fetchPrediction = async () => {
    if (!activeRashi || activeTab === 'daily') return;

    if (isComingSoonTab(activeTab)) {
      setPredictionData([]);
      setError(false);
      return;
    }

    setTextStatus('PENDING');
    setPredictionData([]);
    setVideoUrl(null);
    setIsVideoLoading(true);
    setRatings(null);
    setAudioSrc(null);

    try {
      let res: any;

      if (activeTab === 'weekly') {
        res =
          weeklyView === 'current'
            ? await getWeeklyPrediction({ rashi: activeRashi }).unwrap()
            : await getNextWeekPrediction({ rashi: activeRashi }).unwrap();
      } else {
        if (activeTab === 'monthly') {
          if (monthlyView === 'current') {
            res = await getMonthlyPrediction({ rashi: activeRashi }).unwrap();
          } else {
            // Next month is Coming Soon; comment out the logic to be reused later.
            // res = await getNextMonthlyPrediction({ rashi: activeRashi }).unwrap();
            setPredictionData([]);
            setVideoUrl(null);
            setTextStatus('IDLE');
            return;
          }
        }
      }

      if (!res?.prediction) {
        setRatings(null);
        setTextStatus('FAILED');
        return;
      }

      let parsed: any;

      try {
        parsed = typeof res.prediction === 'string' ? JSON.parse(res.prediction) : res.prediction;
      } catch (err) {
        console.error('Prediction parse error:', err);
        setTextStatus('FAILED');
        return;
      }

      if (activeTab === 'weekly' && parsed?.ratings) {
        setRatings(parsed.ratings);
      } else {
        setRatings(null);
      }

      let formatted: HoroscopeData[] = [];

      if (activeTab === 'weekly') {
        formatted = formatWeeklyPrediction(parsed);
      } else if (activeTab === 'monthly') {
        formatted = formatMonthlyPrediction(parsed);
      } else {
        formatted = formatPredictionText(
          typeof res.prediction === 'string' ? res.prediction : JSON.stringify(res.prediction)
        );
      }

      setPredictionData(formatted);
      setTextStatus(formatted.length ? 'SUCCESS' : 'FAILED');

      if (activeTab === 'weekly' && res?.id) {
        setPredictionId(res.id);
        fetchAudioWithPolling(res.id);
      }

      if (activeTab === 'monthly') {
        const video =
          typeof res === 'object' && res !== null && 'videoUrl' in res
            ? (res.videoUrl ?? null)
            : null;

        setVideoUrl(video);
      }
    } catch (err) {
      setRatings(null);
      setTextStatus('FAILED');
    }
  };

  const fetchWeeklyCharts = async () => {
    setUiLoading(true);
    try {
      const period = weeklyView === 'current' ? 'CurrentWeek' : 'NextWeek';

      const chartRes = await getPredictionPeriod(period as any)?.unwrap();

      if (!chartRes?.charts) {
        setPredictionCharts(null);
        return;
      }

      setPredictionCharts({
        south: chartRes.charts.find((c) => c.chartType === 'south')?.imageUrl,
        north: chartRes.charts.find((c) => c.chartType === 'north')?.imageUrl,
      });
    } catch (err) {
      setPredictionCharts(null);
    } finally {
      setUiLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'weekly') {
      fetchWeeklyCharts();
    } else {
      setPredictionCharts(null);
    }
  }, [activeTab, weeklyView, activeRashi]);

  const reloadPrediction = () => {
    if (!activeRashi) return;

    setPredictionData([]);
    setRatings(null);
    setVideoUrl(null);
    setIsVideoLoading(true);
    setAudioSrc(null);
    setPredictionId(null);
    setTextStatus('PENDING');

    fetchPrediction();
  };

  useEffect(() => {
    fetchPrediction();
  }, [activeRashi, activeTab, weeklyView, monthlyView]);

  const activeRashiLabel = RASHIS.find((r) => r.en === activeRashi)?.hi + ' / ' + activeRashi;

  const getOrdinal = (n: number) => {
    if (n > 3 && n < 21) return 'th';
    switch (n % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };
  const getCurrentWeekRange = () => {
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = day === 0 ? -7 : 0 - day;
    const start = new Date(now);
    start.setDate(now.getDate() + diffToMonday);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const startDay = start.getDate();
    const endDay = end.getDate();
    const monthName = start.toLocaleString('default', { month: 'long' });
    const year = start.getFullYear();
    return `${monthName} ${startDay}${getOrdinal(
      startDay
    )} – ${endDay}${getOrdinal(endDay)}, ${year}`;
  };
  const getNextWeekRange = () => {
    const now = new Date();
    const day = now.getDay();

    const diffToNextMonday = day === 0 ? 1 : 7 - day;

    const start = new Date(now);
    start.setDate(now.getDate() + diffToNextMonday);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    const startDay = start.getDate();
    const endDay = end.getDate();
    const monthName = start.toLocaleString('default', { month: 'long' });
    const year = start.getFullYear();

    return `${monthName} ${startDay}${getOrdinal(
      startDay
    )} – ${endDay}${getOrdinal(endDay)}, ${year}`;
  };

  const getCurrentMonthLabel = () => {
    const now = new Date();
    return `${now.toLocaleString('default', {
      month: 'long',
    })} ${now.getFullYear()}`;
  };

  const getNextMonthLabel = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    return `${nextMonth.toLocaleString('default', {
      month: 'long',
    })} ${nextMonth.getFullYear()}`;
  };

  const dateLabel =
    activeTab === 'weekly'
      ? weeklyView === 'current'
        ? getCurrentWeekRange()
        : getNextWeekRange()
      : monthlyView === 'current'
        ? getCurrentMonthLabel()
        : getNextMonthLabel();

  const activeRashiObj = RASHIS.find((r) => r.en === activeRashi);
  const headingText = `${activeRashiObj?.hi?.toUpperCase()} RASHI (${activeRashiObj?.en.toUpperCase()} MOON SIGN): ${activeTab === 'weekly'
      ? weeklyView === 'current'
        ? 'CURRENT WEEK'
        : 'NEXT WEEK'
      : monthlyView === 'current'
        ? 'CURRENT MONTH'
        : 'NEXT MONTH'
    } PREDICTIONS`;

  return (
    <section className="w-full min-h-screen text-black lg:py-26 font-spectral">
      <div className="max-w-[1600px] mx-auto bg-white gap-8 py-4 ">
        <div className="mb-6 lg:mb-8">
          <TabSlider>
            <button
              onClick={() => setActiveTab('daily')}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${activeTab === 'daily'
                  ? 'bg-gradient-to-r from-[#082261] to-[#34065c] text-white'
                  : 'bg-white inner-shadow-blue text-black'
                }`}
            >
              DAILY PREDICTIONS
            </button>

            <button
              onClick={() => setActiveTab('weekly')}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${activeTab === 'weekly'
                  ? 'bg-gradient-to-r from-[#082261] to-[#34065c] text-white'
                  : 'bg-white inner-shadow-blue text-black'
                }`}
            >
              WEEKLY PREDICTIONS
            </button>

            <button
              onClick={() => setActiveTab('monthly')}
              className={`px-6 py-2 rounded-lg font-semibold transition-all ${activeTab === 'monthly'
                  ? 'bg-gradient-to-r from-[#082261] to-[#34065c] text-white'
                  : 'bg-white inner-shadow-blue text-black'
                }`}
            >
              MONTHLY PREDICTIONS
            </button>

            {/* <button
            onClick={() => setActiveTab('quarterly')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all ${
              activeTab === 'quarterly'
                ? 'bg-gradient-to-r from-[#082261] to-[#34065c] text-white'
                : 'bg-white inner-shadow-blue text-black'
            }`}
          >
            QUARTERLY PREDICTIONS
          </button>

          <button
            onClick={() => setActiveTab('annual')}
            className={`px-6 py-2 rounded-lg font-semibold transition-all ${
              activeTab === 'annual'
                ? 'bg-gradient-to-r from-[#082261] to-[#34065c] text-white'
                : 'bg-white inner-shadow-blue text-black'
            }`}
          >
            ANNUAL PREDICTIONS
          </button> */}
          </TabSlider>
        </div>

        {activeTab === 'monthly' && (
          <div className="mb-6 flex justify-center w-full">
            <SegmentedControl
              options={[
                {
                  label: new Date().toLocaleString('default', { month: 'long' }),
                  value: 'current',
                },
                {
                  label: new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    1
                  ).toLocaleString('default', { month: 'long' }),
                  value: 'next',
                },
              ]}
              value={monthlyView}
              onChange={(val) => setMonthlyView(val as 'current' | 'next')}
            />
          </div>
        )}

        {activeTab === 'weekly' && (
          <div className="mb-6 flex justify-center w-full">
            <SegmentedControl
              options={[
                { label: 'CURRENT WEEK', value: 'current' },
                { label: 'NEXT WEEK', value: 'next' },
              ]}
              value={weeklyView}
              onChange={(val) => setWeeklyView(val as 'current' | 'next')}
            />
          </div>
        )}

        {activeTab === 'daily' ? (
          <DailyPredictionsView mode="general" rashi={getUserMoonSign(user)} />
        ) : (
        <div className="flex gap-8">
          {/* <aside className="w-[260px] shrink-0 sticky top-12 self-start">
            <h3 className="font-spectral font-semibold text-[#082261] text-xl mb-4">
              SELECT RASHI
            </h3>

            <div className="flex flex-col gap-2 uppercase">
              {RASHIS.map((rashi) => (
                <button
                  key={rashi.en}
                  onClick={() => setActiveRashi(rashi.en)}
                  className={`px-4 py-2 rounded-lg text-left font-spectral transition-all ${
                    activeRashi === rashi.en
                      ? 'bg-[linear-gradient(to_right,#082261,#34065c)] text-white'
                      : 'bg-white inner-shadow-blue text-black'
                  }`}
                >
                  {rashi.hi} / {rashi.en}
                </button>
              ))}
            </div>
          </aside> */}

          <main className="flex-1 overflow-y-auto thin-scrollbar">
            {(activeTab === 'weekly' || activeTab === 'monthly') && (
              <div className="relative mb-6">
                <h2
                  className="
                    text-center
                    font-22
                    sm:text-[18px]
                    md:text-[20px]
                    lg:text-[22px]
                    font-semibold
                    font-spectral
                    bg-clip-text
                    text-transparent
                    bg-gradient-to-r
                    from-[#082261]
                    to-[#34065c]
                    uppercase
                    mx-auto
                    max-w-[90%]
                    sm:max-w-[85%]
                    md:max-w-[70%]
                    lg:max-w-[60%]
                    px-3
                    pt-3
                    leading-snug
                    break-words
                  "
                >
                  {headingText}
                </h2>

                <span className="absolute right-0 text-black -translate-y-1/2 top-1/2 font-lato font-18 whitespace-nowrap">
                  {dateLabel}
                </span>
              </div>
            )}

            {activeTab === 'weekly' &&
              (visualLoading ? (
                <div className="flex justify-center py-20">
                  <Loader />
                </div>
              ) : (
                textStatus === 'SUCCESS' &&
                predictionCharts &&
                (predictionCharts.south || predictionCharts.north) &&
                ratings && (
                  <div className="mb-10 rounded-xl md:border md:border-[#2DAAFD] bg-white md:p-6">
                    <div className="grid items-start grid-cols-1 xl:grid-cols-2 xl:gap-8 ">
                      <div className="order-2 md:order-none">
                        <RatingsBarChart ratings={ratings} />
                      </div>

                      <div className="flex flex-col order-1 md:order-none">
                        <h3 className="font-spectral text-[#001B6B] font-semibold text-[16px] md:text-[18px] uppercase mb-6 text-center">
                          Transit Chart South Indian & North Indian
                        </h3>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:border border-[#2DAAFD] rounded-xl gap-3">
                          <div className="py-2 md:p-5 rounded-xl">
                            {predictionCharts?.south ? (
                              <img
                                src={predictionCharts.south}
                                alt="Transit South Chart"
                                className="w-full p-2 border rounded-md cursor-pointer"
                                onClick={() =>
                                  openModal(
                                    [predictionCharts.south, predictionCharts.north],
                                    'Transit Chart South Indian & North Indian'
                                  )
                                }
                              />
                            ) : (
                              <div className="h-[260px] flex items-center justify-center text-sm text-gray-500">
                                Chart not available
                              </div>
                            )}
                          </div>

                          <div className="py-2 md:p-5 rounded-xl">
                            {predictionCharts?.north ? (
                              <img
                                src={predictionCharts.north}
                                alt="Transit North Chart"
                                className="w-full border rounded-md cursor-pointer"
                                onClick={() =>
                                  openModal(
                                    [predictionCharts.south, predictionCharts.north],
                                    'Rashi Chart (South Indian & North Indian)'
                                  )
                                }
                              />
                            ) : (
                              <div className="h-[260px] flex items-center justify-center text-sm text-gray-500">
                                Chart not available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 md:p-8">
                      {audioLoading ? (
                        <p className="text-center font-semibold text-[#082261] animate-pulse">
                          Loading your personalized prediction audio...
                        </p>
                      ) : audioError ? (
                        <div className="text-center">
                          <p className="font-medium text-red-500 mb-2">{audioError}</p>
                          {predictionId && (
                            <button
                              onClick={() => fetchAudioWithPolling(predictionId)}
                              className="px-4 py-2 bg-[#082261] text-white text-sm rounded-lg font-spectral hover:bg-[#2DAAFD] transition-colors"
                            >
                              Regenerate Audio
                            </button>
                          )}
                        </div>
                      ) : (
                        audioSrc && (
                          <>
                            <AudioVisualizer audioSrc={audioSrc} />
                            <p className="mt-6 text-center text-[18px] text-gray-800 font-medium">
                              Click Play to hear your personalized predictions.
                            </p>
                          </>
                        )
                      )}
                    </div>
                  </div>
                )
              ))}

            {activeTab === 'monthly' && monthlyView === 'next' ? (
              /* Next Month Coming Soon Card in project theme colors */
              <div className="flex justify-center w-full py-12">
                <div className="w-full max-w-[600px] bg-gradient-to-br from-[#082261] to-[#34065c] rounded-2xl p-8 border border-[#2DAAFD]/30 shadow-[0_4px_30px_rgba(45,170,253,0.15)] text-center flex flex-col items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-[#2DAAFD]/10 flex items-center justify-center border border-[#2DAAFD]/30 mb-6 shadow-[0_0_15px_rgba(45,170,253,0.2)] animate-pulse">
                    <svg
                      className="w-8 h-8 text-[#2DAAFD]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9.813 15.904L9 21l-.813-5.096L3 15l5.096-.813L9 9l.813 5.187L15 15l-5.187.904z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.071 4.929l-.353 2.213-.354-2.213-2.213-.353 2.213-.354.354 2.213.353-2.213z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-spectral text-2xl md:text-3xl font-bold text-white tracking-widest mb-4 flex items-center gap-2">
                    <span className="text-[#2DAAFD] animate-pulse">✨</span> COMING SOON!{' '}
                    <span className="text-[#2DAAFD] animate-pulse">✨</span>
                  </h3>
                  <div className="w-full max-w-[200px] h-[1px] bg-gradient-to-r from-transparent via-[#2DAAFD]/50 to-transparent mb-6" />
                  <p className="font-lato text-gray-200 text-base md:text-lg leading-relaxed max-w-[420px]">
                    Content and the Video prediction for the next month will be available in the
                    last week of the current month.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'monthly' && (
                  <div className="flex justify-center w-full mb-8 rounded-xl">
                    {videoUrl ? (
                      <div className="relative w-full flex justify-center max-h-[620px] max-w-[1000px] aspect-video rounded-lg bg-black overflow-hidden">
                        {isVideoLoading && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#082261]/20 via-neutral-900 to-[#34065c]/20 animate-pulse border border-[#2DAAFD]/30 rounded-lg">
                            {/* Play button with skeleton */}
                            <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border border-[#2DAAFD]/50 shadow-[0_0_15px_rgba(45,170,253,0.3)]">
                              <svg
                                className="w-10 h-10 text-[#2DAAFD] ml-1"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                              >
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                            <p className="mt-4 font-spectral text-[#2DAAFD] text-sm font-semibold tracking-wider animate-pulse">
                              LOADING PREDICTION VIDEO...
                            </p>
                          </div>
                        )}
                        <video
                          key={videoUrl}
                          src={videoUrl}
                          controls
                          playsInline
                          onLoadedData={() => setIsVideoLoading(false)}
                          className={`w-full flex justify-center max-h-[620px] max-w-[1000px] rounded-lg bg-black ${isVideoLoading ? 'absolute opacity-0 w-0 h-0' : 'block'
                            }`}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center min-h-[180px] text-center px-4">
                        <p className="font-spectral text-[#082261] text-lg">
                          VIDEO AVAILABLE SOON!
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {isComingSoonTab(activeTab) ? (
                  <div className="text-center text-[#082261] font-spectral text-lg mt-10">
                    Coming soon...
                  </div>
                ) : textStatus === 'FAILED' ? (
                  <div className="text-center text-[#082261] font-spectral text-lg">
                    Predictions are not visible, please click the Reload button.
                    <br />
                    <button
                      onClick={reloadPrediction}
                      className="mt-4 px-6 py-2 bg-[#082261] text-white rounded-lg font-spectral"
                    >
                      Reload Prediction
                    </button>
                  </div>
                ) : textStatus === 'PENDING' ? (
                  <div className="text-center text-[#082261] font-spectral text-lg">
                    Decoding your unique planetary alignments to craft your personalized prediction.
                    Please wait a moment...
                  </div>
                ) : predictionData.length ? (
                  <div className="space-y-6">
                    {predictionData.map((section, idx) => {
                      if (section.title === 'HEADING') {
                        return (
                          <h2
                            key={idx}
                            className="font-spectral text-[#001B6B] font-bold text-[22px] uppercase text-center my-6 border-b border-[#001B6B]/20 pb-2"
                          >
                            {section.content}
                          </h2>
                        );
                      }

                      let parsedContent: any = null;
                      try {
                        parsedContent = JSON.parse(section.content);
                      } catch { }

                      // Helper for labels that should be blue & bold within a paragraph
                      const isSectorLabel = (title: string) => {
                        const normalized = title.trim().toLowerCase();
                        return [
                          'career',
                          'wealth',
                          'money',
                          'relationships',
                          'children',
                          'health',
                          'education',
                          'overview',
                          'general predictions',
                          'personal creative life',
                          'phases',
                          'career & profession',
                          'money & investments',
                          'wealth & investments',
                          'health & education',
                          'children creative',
                          'relationships & partnerships',
                          'your monthly predictions',
                          'your weekly predictions',
                          'relationships partnerships',
                          'children creative',
                          'health education',
                          'sector wise predictions',
                          'monthly phases momentum',
                          'phase 1',
                          'phase 2',
                          'phase 3',
                          'wealth investments',
                          'career profession',
                          'children creative endeavors',
                        ].some((lbl) => normalized.includes(lbl));
                      };

                      // Detect Parent Headers (Nested sections starting point)
                      const isParentHeader = section.content === '' && section.title !== '';

                      return (
                        <section key={idx}>
                          {isParentHeader ? (
                            <h3 className="font-spectral text-[#001B6B] font-semibold text-[20px] uppercase tracking-wide mt-8 underline decoration-[#001B6B]/30 underline-offset-4">
                              {section.title}
                            </h3>
                          ) : !parsedContent && isSectorLabel(section.title) ? (
                            <p className="font-lato text-[#333333] text-[18px] leading-relaxed mt-3">
                              <span className="font-semibold text-[#001B6B]">{section.title}:</span>{' '}
                              {section.content}
                            </p>
                          ) : (
                            <>
                              {section.title && section.title !== '' && (
                                <h3 className="font-spectral text-[#001B6B] font-semibold text-[20px] uppercase tracking-wide mt-4">
                                  {section.title}
                                </h3>
                              )}

                              {parsedContent?.type === 'grouped-bullets' ? (
                                <div className="mt-3 space-y-4">
                                  {parsedContent.groups.map(
                                    (group: { subtitle: string; items: string[] }, gi: number) => (
                                      <div key={gi}>
                                        <h4 className="font-lato font-semibold text-[18px] text-[#001B6B] mb-1">
                                          {group.subtitle}
                                        </h4>

                                        <ul className="pl-6 space-y-1 list-disc">
                                          {group.items.map((item, i) => (
                                            <li
                                              key={i}
                                              className="font-lato text-[#333333] text-[17px] leading-relaxed"
                                            >
                                              {item}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : parsedContent?.type === 'bullets' ? (
                                <ul className="pl-6 mt-3 space-y-2 list-disc">
                                  {parsedContent.items.map((item: string, i: number) => {
                                    const colonIndex = item.indexOf(':');
                                    if (colonIndex > 0 && colonIndex < 35) {
                                      const label = item.substring(0, colonIndex).trim();
                                      const content = item.substring(colonIndex + 1).trim();

                                      const shouldHighlight = [
                                        'education',
                                        'health',
                                        'relationships',
                                        'children',
                                        'wealth & investments',
                                        'career & profession',
                                        'health & education',
                                        'wealth and investments',
                                        'career and profession',
                                        'health and education',
                                        'children creative',
                                        'children creative endeavors',
                                        "children's creative endeavors",
                                        'relationships & partnerships',
                                        'phase 1',
                                        'phase 2',
                                        'phase 3',
                                        'overview',
                                        'general predictions',
                                      ].includes(label.toLowerCase());

                                      if (shouldHighlight) {
                                        return (
                                          <li
                                            key={i}
                                            className="font-lato text-[#333333] text-[18px] leading-relaxed"
                                          >
                                            <span className="font-semibold text-[#001B6B]">
                                              {label}:
                                            </span>{' '}
                                            {content}
                                          </li>
                                        );
                                      }
                                    }

                                    return (
                                      <li
                                        key={i}
                                        className="font-lato text-[#333333] text-[18px] leading-relaxed"
                                      >
                                        {item}
                                      </li>
                                    );
                                  })}
                                </ul>
                              ) : (
                                <p className="font-lato text-[#333333] text-[18px] leading-relaxed mt-2">
                                  {section.content}
                                </p>
                              )}
                            </>
                          )}
                        </section>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-[#082261] font-spectral text-lg">
                    No predictions available at the moment.
                    <br />
                    <button
                      onClick={reloadPrediction}
                      className="mt-4 px-6 py-2 bg-[#082261] text-white rounded-lg font-spectral"
                    >
                      Reload Prediction
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
        )}
      </div>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedTitle}
        widthClass="max-w-4xl"
      >
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 w-full">
          {modalImages.map((img, i) => (
            <div key={i} className="flex-1 w-full flex justify-center items-center">
              <img
                src={img}
                alt="Chart"
                className="max-h-[60vh] w-auto max-w-full rounded-md object-contain border border-[#2DAAFD] p-1 bg-white"
              />
            </div>
          ))}
        </div>
      </Modal>
    </section>
  );
}
